import React, { useMemo } from 'react'
import * as LucideIcons from 'lucide-react'
// Import commonly used icons directly to ensure they're available
import {
  Accessibility,
  Wifi,
  ParkingCircle,
  Coffee,
  Wind,
  Calendar,
  Bike,
  CheckCircle2
} from 'lucide-react'

const About = ({ salon }) => {
  // Default values if salon data is not available
  const salonName = salon?.name || 'Timeless Trends Salon'
  const salonDescription = salon?.description || 'Welcome to Timeless Trends Salon, where beauty meets excellence. With over 15 years of experience, we\'ve been transforming lives through our premium salon services. Our team of expert stylists and therapists are dedicated to providing you with an unforgettable experience.'
  const businessHours = salon?.businessHours || {}
  const additionalInfo = salon?.additionalInfo || {}
  
  // Default business hours if not in database
  const defaultHours = {
    'Monday - Friday': '9:00 AM - 8:00 PM',
    'Saturday': '10:00 AM - 6:00 PM',
    'Sunday': '11:00 AM - 5:00 PM'
  }
  
  // Direct icon map for common icons (faster lookup)
  const directIconMap = {
    Accessibility,
    Wifi,
    ParkingCircle,
    Coffee,
    Wind,
    Calendar,
    Bike,
    CheckCircle2
  }
  
  // Dynamic icon resolver - gets any icon from lucide-react by name
  // This allows admin to use any Lucide icon without hardcoding
  const getIconComponent = useMemo(() => {
    // Pre-build a case-insensitive lookup map for faster searching
    const iconLookupMap = {}
    Object.keys(LucideIcons).forEach(key => {
      if (typeof LucideIcons[key] === 'function') {
        const lowerKey = key.toLowerCase()
        if (!iconLookupMap[lowerKey]) {
          iconLookupMap[lowerKey] = key // Store original case key
        }
      }
    })
    
    return (iconName) => {
      if (!iconName || typeof iconName !== 'string') {
        console.warn('⚠️ Invalid icon name:', iconName)
        return CheckCircle2 // Default fallback
      }
      
      // Clean the icon name (remove whitespace)
      const cleanIconName = iconName.trim()
      const cleanLower = cleanIconName.toLowerCase()
      
      // First, try direct icon map (faster for common icons) - case-sensitive
      if (directIconMap[cleanIconName]) {
        console.log('✅ Icon resolved from direct map:', cleanIconName)
        return directIconMap[cleanIconName]
      }
      
      // Try case-insensitive lookup in direct map
      const directMapKey = Object.keys(directIconMap).find(k => k.toLowerCase() === cleanLower)
      if (directMapKey) {
        console.log('✅ Icon resolved from direct map (case-insensitive):', cleanIconName, '→', directMapKey)
        return directIconMap[directMapKey]
      }
      
      // Try exact match in namespace (case-sensitive)
      let IconComponent = LucideIcons[cleanIconName]
      if (IconComponent && typeof IconComponent === 'function') {
        console.log('✅ Icon resolved from namespace (exact):', cleanIconName)
        return IconComponent
      }
      
      // Try case-insensitive lookup in namespace
      const namespaceKey = iconLookupMap[cleanLower]
      if (namespaceKey) {
        IconComponent = LucideIcons[namespaceKey]
        if (IconComponent && typeof IconComponent === 'function') {
          console.log('✅ Icon resolved from namespace (case-insensitive):', cleanIconName, '→', namespaceKey)
          return IconComponent
        }
      }
      
      // If exact match fails, try common variations
      const variations = [
        cleanIconName, // Original
        cleanIconName.charAt(0).toUpperCase() + cleanIconName.slice(1).toLowerCase(), // Title case
        cleanIconName.toLowerCase(), // Lowercase
        cleanIconName.toUpperCase(), // Uppercase
      ]
      
      for (const variant of variations) {
        // Try direct map first
        if (directIconMap[variant]) {
          console.log('✅ Icon resolved via variation (direct):', cleanIconName, '→', variant)
          return directIconMap[variant]
        }
        // Then try namespace
        IconComponent = LucideIcons[variant]
        if (IconComponent && typeof IconComponent === 'function') {
          console.log('✅ Icon resolved via variation (namespace):', cleanIconName, '→', variant)
          return IconComponent
        }
      }
      
      // Try fuzzy matching - find icons with similar names
      if (cleanIconName && !cleanIconName.includes('Default')) {
        const searchTerm = cleanLower
        const availableIcons = Object.keys(LucideIcons).filter(key => {
          if (typeof LucideIcons[key] !== 'function') return false
          const keyLower = key.toLowerCase()
          // Check if search term is contained in key or vice versa
          return keyLower === searchTerm || 
                 keyLower.includes(searchTerm) || 
                 searchTerm.includes(keyLower) ||
                 keyLower.startsWith(searchTerm) || 
                 searchTerm.startsWith(keyLower)
        }).slice(0, 10)
        
        console.warn('⚠️ Icon not found:', cleanIconName)
        if (availableIcons.length > 0) {
          console.warn('  Available similar icons:', availableIcons)
          // Try the first similar icon as a fallback (exact match first, then partial)
          const exactMatch = availableIcons.find(icon => icon.toLowerCase() === searchTerm)
          const iconToUse = exactMatch || availableIcons[0]
          const similarIcon = LucideIcons[iconToUse]
          if (similarIcon && typeof similarIcon === 'function') {
            console.log('  → Using similar icon:', iconToUse)
            return similarIcon
          }
        } else {
          // Show all available icon names that start with the same letter
          const firstLetter = cleanIconName.charAt(0).toUpperCase()
          const iconsStartingWith = Object.keys(LucideIcons).filter(key => 
            typeof LucideIcons[key] === 'function' && 
            key.toLowerCase().startsWith(firstLetter.toLowerCase())
          ).slice(0, 10)
          if (iconsStartingWith.length > 0) {
            console.warn('  Icons starting with "' + firstLetter + '":', iconsStartingWith)
          }
        }
      }
      
      // Fallback to default icon
      console.warn('  → Falling back to CheckCircle2')
      return CheckCircle2
    }
  }, [])
  
  // Legacy icon map for backward compatibility (text-based icon detection)
  const lucideIconMap = {
    Accessibility,
    Wifi,
    ParkingCircle,
    Coffee,
    Wind,
    Calendar,
    Bike,
    Default: CheckCircle2
  }

  // Function to map text to Lucide icon key
  const getIconForText = (text) => {
    const textLower = text.toLowerCase()
    if (textLower.includes('wifi')) return 'Wifi'
    if (textLower.includes('parking')) return 'ParkingCircle'
    if (textLower.includes('wheelchair') || textLower.includes('accessible')) return 'Accessibility'
    if (textLower.includes('coffee') || textLower.includes('refreshment')) return 'Coffee'
    if (textLower.includes('wind') || textLower.includes('air') || textLower.includes('breeze')) return 'Wind'
    if (textLower.includes('calendar') || textLower.includes('appointment') || textLower.includes('booking')) return 'Calendar'
    return 'Default' // Fallback Lucide icon
  }
  
  // Default amenities if not in database
  const defaultAmenities = [
    { icon: 'Wifi', label: 'Free WiFi' },
    { icon: 'ParkingCircle', label: 'Parking' },
    { icon: 'Accessibility', label: 'Accessible' },
    { icon: 'Coffee', label: 'Refreshments' },
    { icon: 'Wind', label: 'Comfortable Environment' },
    { icon: 'Calendar', label: 'Easy Appointments' }
  ]
  
  // Use business hours from database or defaults
  const displayHours = Object.keys(businessHours).length > 0 ? businessHours : defaultHours
  
  // Function to get the priority of a day string for sorting
  const getDayPriority = (dayString) => {
    const dayOrder = {
      'monday': 1,
      'tuesday': 2,
      'wednesday': 3,
      'thursday': 4,
      'friday': 5,
      'saturday': 6,
      'sunday': 7
    }
    
    // Convert to lowercase and get first word (handles "Monday - Friday" or "Monday")
    const firstDay = dayString.toLowerCase().split(/[\s-]+/)[0]
    return dayOrder[firstDay] || 999 // Put unknown entries at the end
  }
  
  // Sort business hours by day order
  const sortedHours = Object.entries(displayHours).sort((a, b) => {
    return getDayPriority(a[0]) - getDayPriority(b[0])
  })

  // Format day labels so they start with capital letters (e.g. "monday - friday" → "Monday - Friday")
  const formatDayLabel = (dayString) => {
    if (!dayString || typeof dayString !== 'string') return ''
    // Split on "-" to preserve ranges like "monday - friday"
    return dayString
      .split('-')
      .map(part => part.trim())
      .map(part => {
        if (!part) return ''
        return part.charAt(0).toUpperCase() + part.slice(1)
      })
      .join(' - ')
  }
  
  // Helper to format amenity labels from keys (e.g. "free_parking" → "Free parking")
  const formatAmenityLabel = (key) => {
    if (!key || typeof key !== 'string') return ''
    // Replace underscores with spaces, split camelCase, then capitalize first letter
    const withSpaces = key
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .toLowerCase()
    return withSpaces
      .split(' ')
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }
  
  // Parse and normalize amenities from various formats
  const parseAmenities = () => {
    // Format 1: { amenities: [{icon, title}, ...] } or { amenities: ["Free WiFi", "Parking"] }
    if (additionalInfo.amenities) {
      // 1a) amenities is already an array
      if (Array.isArray(additionalInfo.amenities)) {
        // If it's an array of strings: ["Free WiFi", "Parking"]
        if (additionalInfo.amenities.length > 0 && typeof additionalInfo.amenities[0] === 'string') {
        return additionalInfo.amenities.map(text => ({
          icon: getIconForText(text),
          label: text
        }))
        }

        // If it's an array of objects from admin: [{ id, icon, order, title }, ...]
        if (additionalInfo.amenities.length > 0 && typeof additionalInfo.amenities[0] === 'object') {
          return additionalInfo.amenities
            // Preserve admin-defined order if present
            .slice()
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((item, idx) => {
              const rawIcon = item.icon || ''
              const hasTitle = typeof item.title === 'string' && item.title.trim().length > 0
              const hasLabel = typeof item.label === 'string' && item.label.trim().length > 0
              const label = hasTitle
                ? item.title.trim()
                : hasLabel
                  ? item.label.trim()
                  : formatAmenityLabel(rawIcon)

              // Use dynamic icon resolver to get any Lucide icon by name
              // This allows admin to use any icon from lucide-react
              const iconKey = rawIcon || 'Default'

              return {
                id: item.id || `amenity-${idx}`, // Preserve ID for React key
                icon: iconKey,
                label
              }
            })
        }
      }

      // 1b) amenities is an object of flags/strings: { free_parking: true, wifi: true }
      if (!Array.isArray(additionalInfo.amenities) && typeof additionalInfo.amenities === 'object') {
        const entries = Object.entries(additionalInfo.amenities)
          .filter(([key, value]) => {
            if (typeof value === 'boolean') return value === true
            if (typeof value === 'string') return value.trim().length > 0
            return false
          })
          .map(([key, value]) => {
            const label = typeof value === 'string' && value.trim().length > 0
              ? value.trim()
              : formatAmenityLabel(key)
            return {
              icon: getIconForText(label),
              label
            }
          })

        if (entries.length > 0) {
          return entries
        }
      }
    }
    
    // Format 2: Direct array of strings in additional_info
    if (Array.isArray(additionalInfo) && additionalInfo.length > 0) {
      return additionalInfo.map(text => ({
        icon: getIconForText(text),
        label: text
      }))
    }
    
    // Format 3: Check if additional_info itself is an array of strings at root level
    if (Object.keys(additionalInfo).length > 0 && !additionalInfo.amenities) {
      // Check if additionalInfo has numeric keys (it's actually an array)
      const keys = Object.keys(additionalInfo)
      if (keys.every(key => !isNaN(key))) {
        return Object.values(additionalInfo).map(text => ({
          icon: getIconForText(text),
          label: text
        }))
      }
    }

    // Format 4: additional_info as an object of boolean flags or descriptive strings, e.g.
    // { free_parking: true, wifi: true, private_rooms: \"Private rooms available\" }
    if (additionalInfo && typeof additionalInfo === 'object' && Object.keys(additionalInfo).length > 0) {
      const entries = Object.entries(additionalInfo)
        .filter(([key, value]) => {
          if (typeof value === 'boolean') return value === true
          if (typeof value === 'string') return value.trim().length > 0
          return false
        })
        .map(([key, value]) => {
          const label = typeof value === 'string' && value.trim().length > 0
            ? value.trim()
            : formatAmenityLabel(key)
          return {
            icon: getIconForText(label),
            label
          }
        })

      if (entries.length > 0) {
        return entries
      }
    }
    
    // Default fallback
    return defaultAmenities
  }
  
  const displayAmenities = parseAmenities()
  // Debug: log what we're actually rendering so issues are easier to trace
  console.log('🧾 About section amenities:', {
    rawAdditionalInfo: additionalInfo,
    displayAmenities,
    amenitiesCount: displayAmenities?.length || 0
  })
  
  // Log each amenity's icon resolution for debugging
  if (displayAmenities && displayAmenities.length > 0) {
    console.log('🔍 Debugging icon resolution:')
    displayAmenities.forEach((amenity, idx) => {
      const IconComponent = getIconComponent(amenity.icon)
      const iconResolved = IconComponent !== CheckCircle2 || amenity.icon === 'Default'
      console.log(`  Amenity ${idx + 1}:`, {
        icon: amenity.icon,
        label: amenity.label,
        iconResolved: iconResolved,
        iconComponent: IconComponent?.name || IconComponent?.displayName || 'Unknown',
        isDefault: IconComponent === CheckCircle2,
        inDirectMap: amenity.icon in directIconMap,
        inNamespace: amenity.icon in LucideIcons
      })
    })
  }
  
  // Log each amenity's icon resolution for debugging
  if (displayAmenities && displayAmenities.length > 0) {
    displayAmenities.forEach((amenity, idx) => {
      const IconComponent = getIconComponent(amenity.icon)
      const iconResolved = IconComponent !== LucideIcons.CheckCircle2 || amenity.icon === 'Default'
      console.log(`  Amenity ${idx + 1}:`, {
        icon: amenity.icon,
        label: amenity.label,
        iconResolved: iconResolved,
        iconComponent: IconComponent?.name || 'Unknown'
      })
    })
  }
  
  return (
    <section id="about" className="py-12 sm:py-16 lg:py-20 px-2 sm:px-4 md:px-6 lg:px-8">
      <div className="max-w-6xl xl:max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
              About {salonName}
            </h2>
            <div className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 text-justify whitespace-pre-line">
              {salonDescription}
            </div>
            
            {/* Business Hours */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg mb-6 sm:mb-8">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                Business Hours
              </h3>
              <div className="space-y-2 sm:space-y-3">
                {sortedHours.map(([day, hours]) => (
                  <div key={day} className="flex justify-between text-sm sm:text-base">
                    <span className="text-gray-600">{formatDayLabel(day)}</span>
                    <span className="font-semibold">{hours}</span>
                </div>
                ))}
              </div>
            </div>
            
            {/* Amenities / Additional Info */}
            {displayAmenities && displayAmenities.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                {displayAmenities.map((amenity, index) => {
                  // Use dynamic icon resolver to get the icon component
                  // This supports any Lucide icon name from the database
                  const IconComponent = getIconComponent(amenity.icon)
                  // Use amenity ID if available, otherwise fallback to index
                  const amenityKey = amenity.id || `amenity-${index}`
                  return (
                  <div key={amenityKey} className="flex items-center bg-white p-3 sm:p-4 rounded-lg shadow">
                      <IconComponent className="text-primary w-5 h-5 sm:w-6 sm:h-6 mr-3" />
                    <span className="text-sm sm:text-base font-medium">{amenity.label}</span>
              </div>
                  )
                })}
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <img 
              src="https://images.unsplash.com/photo-1562322140-8baeececf3df?w=600&h=800&fit=crop" 
              alt="Salon" 
              className="rounded-xl shadow-lg w-full h-48 sm:h-64 lg:h-80 object-cover"
              loading="lazy"
            />
            <img 
              src="https://images.unsplash.com/photo-1633681926022-84c23e8cb2d6?w=600&h=800&fit=crop" 
              alt="Salon" 
              className="rounded-xl shadow-lg w-full h-48 sm:h-64 lg:h-80 object-cover mt-6 sm:mt-8"
              loading="lazy"
            />
            <img 
              src="https://images.unsplash.com/photo-1519415510236-718bdfcd89c8?w=600&h=800&fit=crop" 
              alt="Salon" 
              className="rounded-xl shadow-lg w-full h-48 sm:h-64 lg:h-80 object-cover -mt-6 sm:-mt-8"
              loading="lazy"
            />
            <img 
              src="https://images.unsplash.com/photo-1521590832167-7bcbfaa6381f?w=600&h=800&fit=crop" 
              alt="Salon" 
              className="rounded-xl shadow-lg w-full h-48 sm:h-64 lg:h-80 object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  )
}

export default About

